// AuthApi — one thin method per endpoint (contract 06 §6, canonical api/ contract 03 §15).
// Imports ONLY an http instance + own models. Returns the unwrapped DTO (flat auth responses),
// never an AxiosResponse. No error handling here — ApiError propagates to AuthService (the
// error boundary). `suppressGlobalErrorHandler` is the class default: auth owns its error UX.
import type { AxiosInstance } from 'axios'

import type {
  AcceptInvitesResponse,
  ChangePasswordCodePayload,
  ChangePasswordPayload,
  ChangePasswordPayloadV2,
  ChangeSettingsPasswordPayload,
  CreateFirstAccountResponse,
  ForgotPasswordPayload,
  ForgotPasswordResponse,
  InviteAccessPayload,
  LoginApiResponse,
  LoginCredentials,
  MfaResendPayload,
  MfaResendResponse,
  MfaVerifyPayload,
  MfaVerifyResponse,
  PasswordResetHandoffExchangePayload,
  PasswordResetHandoffExchangeResponse,
  PendingInvitesResponse,
  ShellHandoffPayload,
  ShellHandoffResponse,
  SignUpCredentials,
  SignupApiResponse,
  TokenHandoffExchangePayload,
  TokenHandoffExchangeResponse,
  TotpConfirmPayload,
  TotpSetupResponse,
  TotpStatusResponse,
} from '../models/auth.types'

import { appHttp, unwrap } from '@/shared/http'
import { resolveSiteLanguage } from '@/shared/i18n'

export class AuthApi {
  // Injectable for tests; defaults to the singleton instance.
  constructor(private readonly http: AxiosInstance = appHttp) {}

  // Auth endpoints own their local error presentation, but only public credential flows own 401s.
  // A bearer-backed auth endpoint must still reach the global session-expiry logout handler.
  private readonly publicCfg = {
    suppressGlobalErrorHandler: true,
    meta: { ownsUnauthorized: true },
  } as const

  private readonly protectedCfg = {
    suppressGlobalErrorHandler: true,
  } as const

  async login(body: LoginCredentials): Promise<LoginApiResponse> {
    return unwrap(
      await this.http.post<LoginApiResponse>(
        '/auth/login_v2/',
        { ...body, language: resolveSiteLanguage() },
        this.publicCfg,
      ),
    )
  }

  /** Signup and Shopify register share this endpoint; Shopify adds opaque `shop`/`shopify_context`. */
  async register(body: SignUpCredentials): Promise<SignupApiResponse> {
    return unwrap(
      await this.http.post<SignupApiResponse>(
        '/auth/register/',
        { ...body, language: resolveSiteLanguage() },
        this.publicCfg,
      ),
    )
  }

  async verifyMfa(body: MfaVerifyPayload): Promise<MfaVerifyResponse> {
    return unwrap(await this.http.post<MfaVerifyResponse>('/auth/verify-mfa', body, this.publicCfg))
  }

  async resendMfa(body: MfaResendPayload): Promise<MfaResendResponse> {
    return unwrap(await this.http.post<MfaResendResponse>('/auth/resend-mfa', body, this.publicCfg))
  }

  async logout(): Promise<void> {
    await this.http.post('/auth/logout/', undefined, this.publicCfg)
  }

  async forgotPassword(body: ForgotPasswordPayload): Promise<ForgotPasswordResponse> {
    return unwrap(
      await this.http.post<ForgotPasswordResponse>('/auth/forget_password/', body, this.publicCfg),
    )
  }

  async exchangeResetHandoff(
    body: PasswordResetHandoffExchangePayload,
  ): Promise<PasswordResetHandoffExchangeResponse> {
    return unwrap(
      await this.http.post<PasswordResetHandoffExchangeResponse>(
        '/auth/password-reset/exchange/',
        body,
        this.publicCfg,
      ),
    )
  }

  /** One endpoint, two credential shapes: the backend branches on `reset_token` vs the legacy
   *  `code` (v3 overlap). The union keeps that choice typed instead of stringly-guessed. */
  async changePassword(body: ChangePasswordPayload | ChangePasswordCodePayload): Promise<void> {
    await this.http.post('/auth/change_password/', body, this.publicCfg)
  }

  async teamJoinSetPassword(body: ChangePasswordPayloadV2): Promise<void> {
    await this.http.post('/auth/team-join/set-password/', body, this.publicCfg)
  }

  /** Settings password change — authenticated by the bearer, NOT by a reset token, and NOT the
   *  team-join route above (v3 aimed both at this one path; BE-AUTH-06 split them). */
  async changeSettingsPassword(body: ChangeSettingsPasswordPayload): Promise<void> {
    await this.http.post('/auth/settings/change_password/', body, this.protectedCfg)
  }

  // The authenticator-app routes are bearer routes, so unlike `cfg` a 401 here IS a dead session:
  // `ownsUnauthorized` stays off and the standard logout path applies. A wrong password answers
  // `400 INVALID_OLD_PASSWORD`, never a 401.
  private readonly totpCfg = { suppressGlobalErrorHandler: true } as const

  async getTotpStatus(): Promise<TotpStatusResponse> {
    return unwrap(await this.http.get<TotpStatusResponse>('/auth/mfa/totp/', this.totpCfg))
  }

  async setupTotp(): Promise<TotpSetupResponse> {
    return unwrap(
      await this.http.post<TotpSetupResponse>('/auth/mfa/totp/setup/', undefined, this.totpCfg),
    )
  }

  async confirmTotp(body: TotpConfirmPayload): Promise<TotpStatusResponse> {
    return unwrap(
      await this.http.post<TotpStatusResponse>('/auth/mfa/totp/confirm/', body, this.totpCfg),
    )
  }

  async disableTotp(): Promise<TotpStatusResponse> {
    return unwrap(await this.http.delete<TotpStatusResponse>('/auth/mfa/totp/', this.totpCfg))
  }

  async exchangeTokenHandoff(
    body: TokenHandoffExchangePayload,
  ): Promise<TokenHandoffExchangeResponse> {
    return unwrap(
      await this.http.post<TokenHandoffExchangeResponse>('/auth/token/exchange/', body, this.publicCfg),
    )
  }

  /** One-time key for the MCP shell (ORP-27464) — the signed-in user's way into mcp.orphex.co. */
  async requestShellHandoff(body: ShellHandoffPayload): Promise<ShellHandoffResponse> {
    return unwrap(
      await this.http.post<ShellHandoffResponse>('/auth/v2/shell-handoff/', body, this.protectedCfg),
    )
  }

  async requestInviteAccess(accountId: string, body: InviteAccessPayload): Promise<void> {
    await this.http.post(
      `/accounts/${encodeURIComponent(accountId)}/users/access-to-request-new-invite-mail`,
      body,
      this.protectedCfg,
    )
  }

  async getPendingInvites(): Promise<PendingInvitesResponse> {
    return unwrap(
      await this.http.get<PendingInvitesResponse>('/auth/pending-invites/', this.protectedCfg),
    )
  }

  async acceptPendingInvites(inviteIds: number[]): Promise<AcceptInvitesResponse> {
    return unwrap(
      await this.http.post<AcceptInvitesResponse>(
        '/auth/pending-invites/accept/',
        { invite_ids: inviteIds },
        this.protectedCfg,
      ),
    )
  }

  async createFirstAccount(): Promise<CreateFirstAccountResponse> {
    return unwrap(
      await this.http.post<CreateFirstAccountResponse>(
        '/auth/accounts/create-first/',
        {},
        this.protectedCfg,
      ),
    )
  }
}

export const authApi = new AuthApi()
